import { useState, useEffect, useRef } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { LayoutDashboard, Users, Calendar, Settings, Search, HelpCircle, LogOut, X, Clock, Shield, Menu, ChevronLeft, ChevronRight, Heart, List } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@hooks/useAuthStore'
import { appointmentApi } from '@services/appointment.service'
import { performLogout } from '@services/api'
import { Input } from '@components/ui/input'
import { SkipLink } from './SkipLink'
import { ClinicSwitcher } from '@components/clinic/ClinicSwitcher'
import { NotificationBell } from './NotificationBell'
import { HelpDialog } from './HelpDialog'
import { cn } from '@lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@components/ui/tooltip'

const SIDEBAR_KEY = 'hms-sidebar-collapsed'

type NavItem = {
  key: string
  path: string
  icon: React.ComponentType<{ className?: string }>
  badge?: number
}

type NavGroup = {
  label: string
  items: NavItem[]
}

const roleLabels: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Administrador',
  doctor: 'Médico',
  nurse: 'Enfermería',
  receptionist: 'Recepción',
  pharmacist: 'Farmacia',
  lab_technician: 'Laboratorio',
  billing_staff: 'Facturación',
  patient: 'Paciente',
}

export function BaseLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const [searchQuery, setSearchQuery] = useState('')
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_KEY)
      if (stored !== null) return stored === 'true'
    } catch {}
    // No stored preference: collapse by default on tablet (md–lg), expand on desktop.
    if (typeof window !== 'undefined') return window.innerWidth < 1024
    return false
  })

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, String(isCollapsed))
    } catch {}
  }, [isCollapsed])

  // Global keyboard shortcuts: Cmd/Ctrl+K focuses search, "?" opens help.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const isTyping =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        searchInputRef.current?.focus()
        return
      }

      if (e.key === '?' && !isTyping && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault()
        setHelpOpen(true)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const { data: pendingData } = useQuery({
    queryKey: ['pending-approvals'],
    queryFn: () => appointmentApi.getPendingApprovals(0, 1),
    enabled: user?.role === 'doctor',
    refetchInterval: 60_000,
  })
  const pendingCount = pendingData?.total ?? 0

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'
  const isDoctor = user?.role === 'doctor'

  // Ordered by clinical importance and split into labelled groups.
  // La agenda es la pantalla principal de la clínica. Facturación y
  // medicamentos siguen disponibles por URL, pero salen del menú: no se usan
  // en la operación diaria y solo estorbaban.
  const navGroups: NavGroup[] = [
    {
      label: t('sidebar.group.main'),
      items: [
        {
          key: 'sidebar.agenda',
          path: '/agenda',
          icon: Calendar,
          badge: isDoctor && pendingCount > 0 ? pendingCount : undefined,
        },
        { key: 'sidebar.home', path: '/dashboard', icon: LayoutDashboard },
      ],
    },
    {
      label: t('sidebar.group.clinical'),
      items: [
        { key: 'sidebar.patients', path: '/patients', icon: Users },
        { key: 'sidebar.appointments', path: '/appointments', icon: List },
        ...(isDoctor
          ? [{ key: 'sidebar.availability', path: '/my-availability', icon: Clock }]
          : []),
      ],
    },
    ...(isAdmin
      ? [
          {
            label: t('sidebar.group.system'),
            items: [{ key: 'sidebar.audit', path: '/audit', icon: Shield }],
          },
        ]
      : []),
  ]

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard'
    if (path === '/agenda') return location.pathname === '/' || location.pathname === '/agenda'
    return location.pathname.startsWith(path)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/patients?search=${encodeURIComponent(searchQuery)}`)
    }
  }

  const roleLabel = roleLabels[user?.role ?? ''] ?? 'Usuario'
  const fullName = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()
  const displayName = `${isDoctor ? 'Dr. ' : ''}${fullName}`.trim()

  return (
    <div className="flex min-h-screen bg-ml-surface overflow-x-hidden">
      <SkipLink targetId="main-content" />

      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ─── SIDEBAR — dark institutional navigation panel ─── */}
      <TooltipProvider delayDuration={300}>
        <aside
          id="main-sidebar"
          className={cn(
            'fixed left-0 top-0 h-screen z-50 flex flex-col text-white',
            // Themed institutional gradient: brand-deep-2 (top) → brand-deep (floor),
            // both derived per-theme from the active primary so the sidebar recolors
            // with the palette. White text stays AA on both (see theme.specialty.test).
            'bg-gradient-to-b from-[rgb(var(--ml-brand-deep-2))] to-[rgb(var(--ml-brand-deep))]',
            'border-r border-white/[0.07]',
            'shadow-[2px_0_24px_rgba(0,0,0,0.28)]',
            'transition-all duration-300 ease-out',
            // Full-width drawer on mobile; collapsible rail on tablet/desktop.
            'w-64',
            isCollapsed && 'md:w-[4.5rem]',
            // Slide in/out on mobile
            isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          )}
        >
          {/* ── HEADER: Brand + active physician ── */}
          <div className="shrink-0 border-b border-white/[0.07]">
            {/* Brand row */}
            <div className="flex items-center h-16 px-3 gap-2.5">
              {/* Logo mark */}
              <Link
                to="/agenda"
                onClick={() => setIsMobileOpen(false)}
                className="shrink-0 h-9 w-9 rounded-lg bg-[rgb(var(--ml-brand-hi))] flex items-center justify-center shadow-sm min-h-0"
                title="MediLux — Inicio"
              >
                <Heart className="h-5 w-5 text-white" />
              </Link>

              {/* Wordmark — hidden when collapsed on tablet/desktop */}
              <Link
                to="/agenda"
                onClick={() => setIsMobileOpen(false)}
                className={cn('flex-1 overflow-hidden min-h-0', isCollapsed && 'md:hidden')}
              >
                <span className="block font-headline italic text-white text-xl leading-none">
                  MediLux
                </span>
                <span className="block text-[9px] uppercase tracking-[0.2em] text-white/40 font-body leading-none mt-1">
                  Clinical Atelier
                </span>
              </Link>

              {/* Desktop/tablet collapse toggle */}
              <button
                onClick={() => setIsCollapsed((v) => !v)}
                className="hidden md:flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/45 hover:text-white hover:bg-white/[0.08] transition-colors min-h-0"
                aria-label={isCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronLeft className="h-4 w-4" />
                )}
              </button>

              {/* Mobile close button */}
              <button
                className="md:hidden h-7 w-7 shrink-0 flex items-center justify-center rounded-md text-white/50 hover:text-white hover:bg-white/[0.08] transition-colors min-h-0"
                onClick={() => setIsMobileOpen(false)}
                aria-label="Cerrar menú"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* ── NAVIGATION ── */}
          <nav
            className="flex-1 overflow-y-auto py-3 scrollbar-hide"
            aria-label="Navegación principal"
          >
            {navGroups.map((group) => (
              <div key={group.label} className="mb-4">
                {/* Group label — text when expanded, divider when collapsed */}
                <div
                  className={cn(
                    'h-5 flex items-center mb-1',
                    isCollapsed ? 'md:justify-center md:px-2' : 'px-4',
                  )}
                >
                  <span
                    className={cn(
                      'text-[10px] font-bold uppercase tracking-[0.16em] text-white/35 select-none',
                      isCollapsed && 'md:hidden',
                    )}
                  >
                    {group.label}
                  </span>
                  <span
                    className={cn(
                      'hidden h-px w-6 bg-white/10',
                      isCollapsed && 'md:block',
                    )}
                  />
                </div>

                <div className="space-y-1 px-2.5">
                  {group.items.map((item) => {
                    const Icon = item.icon
                    const active = isActive(item.path)
                    const label = t(item.key)
                    return (
                      <NavItemWrapper key={item.path} collapsed={isCollapsed} label={label}>
                        <Link
                          to={item.path}
                          onClick={() => setIsMobileOpen(false)}
                          aria-current={active ? 'page' : undefined}
                          className={cn(
                            'relative flex items-center rounded-lg transition-colors duration-150 min-h-0',
                            isCollapsed
                              ? 'gap-3 px-3 py-2.5 md:justify-center md:px-0'
                              : 'gap-3 px-3 py-2.5',
                            active
                              ? 'bg-white/[0.12] text-white font-semibold'
                              : 'text-white/60 hover:text-white hover:bg-white/[0.06]',
                          )}
                        >
                          {/* Left accent bar on the active item */}
                          {active && (
                            <span
                              className={cn(
                                'absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-[rgb(var(--ml-brand-glow))] rounded-r-full',
                                isCollapsed && 'md:hidden',
                              )}
                            />
                          )}

                          <Icon
                            className={cn(
                              'h-[18px] w-[18px] shrink-0',
                              active ? 'text-white' : 'text-white/70',
                            )}
                          />

                          <span className={cn('flex-1 text-sm', isCollapsed && 'md:hidden')}>
                            {label}
                          </span>

                          {/* Notification badge */}
                          {item.badge != null && item.badge > 0 && (
                            <>
                              <span
                                className={cn(
                                  'flex h-5 min-w-5 items-center justify-center rounded bg-amber-500 px-1.5 text-[10px] font-bold text-[#0f2b23]',
                                  isCollapsed && 'md:hidden',
                                )}
                              >
                                {item.badge > 99 ? '99+' : item.badge}
                              </span>
                              {/* Compact badge overlay when collapsed */}
                              <span
                                className={cn(
                                  'absolute -top-0.5 -right-0.5 hidden h-[18px] min-w-[18px] items-center justify-center rounded bg-amber-500 px-1 text-[9px] font-bold text-[#0f2b23]',
                                  isCollapsed && 'md:flex',
                                )}
                              >
                                {item.badge > 99 ? '99+' : item.badge}
                              </span>
                            </>
                          )}
                        </Link>
                      </NavItemWrapper>
                    )
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* ── FOOTER: identity + Settings + Logout ── */}
          <div className="shrink-0 border-t border-white/[0.07] p-2.5 space-y-1">
            {/* Settings */}
            <NavItemWrapper collapsed={isCollapsed} label={t('sidebar.settings')}>
              <Link
                to="/settings"
                onClick={() => setIsMobileOpen(false)}
                aria-current={isActive('/settings') ? 'page' : undefined}
                className={cn(
                  'relative flex items-center gap-3 rounded-lg px-3 py-2 transition-colors duration-150 min-h-0',
                  isCollapsed && 'md:justify-center md:px-0',
                  isActive('/settings')
                    ? 'bg-white/[0.12] text-white font-semibold'
                    : 'text-white/55 hover:text-white hover:bg-white/[0.06]',
                )}
              >
                {isActive('/settings') && (
                  <span
                    className={cn(
                      'absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-teal-300 rounded-r-full',
                      isCollapsed && 'md:hidden',
                    )}
                  />
                )}
                <Settings className="h-[18px] w-[18px] shrink-0" />
                <span className={cn('text-sm', isCollapsed && 'md:hidden')}>
                  {t('sidebar.settings')}
                </span>
              </Link>
            </NavItemWrapper>

            {/* Logout */}
            <NavItemWrapper collapsed={isCollapsed} label={t('sidebar.logout')}>
              <button
                onClick={() => performLogout()}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2 transition-colors duration-150 text-red-300/80 hover:text-red-200 hover:bg-red-500/10 min-h-0',
                  isCollapsed && 'md:justify-center md:px-0',
                )}
              >
                <LogOut className="h-[18px] w-[18px] shrink-0" />
                <span className={cn('text-sm', isCollapsed && 'md:hidden')}>
                  {t('sidebar.logout')}
                </span>
              </button>
            </NavItemWrapper>
          </div>
        </aside>
      </TooltipProvider>

      {/* ─── MAIN CONTENT ─── */}
      <div
        className={cn(
          'flex-1 min-h-screen min-w-0 overflow-x-hidden transition-all duration-300',
          'md:ml-64',
          isCollapsed && 'md:ml-[4.5rem]',
        )}
      >
        {/* Top header bar */}
        <header
          className={cn(
            'fixed top-0 right-0 h-16 z-40',
            'bg-ml-surface/80 backdrop-blur-xl',
            'flex items-center gap-3 px-4 lg:px-8',
            'shadow-[0_1px_0_rgba(3,36,26,0.06)]',
            'transition-all duration-300',
            'w-full md:w-[calc(100%-16rem)]',
            isCollapsed && 'md:w-[calc(100%-4.5rem)]',
          )}
        >
          {/* Hamburger — mobile only */}
          <button
            className="md:hidden shrink-0 p-2 text-ml-on-surface-variant hover:text-ml-on-surface rounded-lg hover:bg-ml-surface-container-high transition-colors min-h-0"
            onClick={() => setIsMobileOpen(true)}
            aria-label="Abrir menú lateral"
            aria-expanded={isMobileOpen}
            aria-controls="main-sidebar"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Search */}
          <form
            onSubmit={handleSearch}
            className="flex items-center flex-1 min-w-0 max-w-md"
            role="search"
          >
            <div className="relative w-full group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ml-on-surface-variant/60 group-focus-within:text-ml-primary transition-colors" />
              <Input
                ref={searchInputRef}
                type="search"
                placeholder={`${t('common.search', 'Buscar')} pacientes...`}
                className="w-full bg-ml-surface-container-high border-none rounded py-2 pl-10 pr-4 text-sm font-medium focus:ring-1 focus:ring-ml-primary/20 transition-all min-h-[40px]"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Buscar pacientes"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ml-on-surface-variant/60 hover:text-ml-primary p-1 min-h-0"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </form>

          {/* Right actions */}
          <div className="flex items-center gap-2 lg:gap-3 shrink-0 ml-auto">
            <div className="hidden sm:block">
              <ClinicSwitcher />
            </div>
            <NotificationBell />
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="hidden lg:flex p-2 text-ml-on-surface-variant hover:text-ml-primary transition-colors min-h-0"
              aria-label={t('help.title')}
            >
              <HelpCircle className="h-5 w-5" />
            </button>
            <div className="hidden lg:block w-px h-5 bg-ml-outline-variant/20" />
            <div className="hidden lg:flex flex-col leading-tight">
              <span className="text-sm font-semibold text-ml-on-surface/80">
                {displayName}
              </span>
              <span className="text-[11px] text-ml-on-surface-variant/60">{roleLabel}</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div
          id="main-content"
          className="pt-24 px-4 sm:px-6 lg:px-10 pb-12 max-w-7xl mx-auto w-full min-w-0"
          tabIndex={-1}
          role="main"
        >
          <Outlet />
        </div>
      </div>

      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  )
}

/** Wraps an item in a tooltip when the sidebar is collapsed (tablet/desktop only). */
function NavItemWrapper({
  collapsed,
  label,
  children,
}: {
  collapsed: boolean
  label: string
  children: React.ReactNode
}) {
  if (!collapsed) return <>{children}</>
  return (
    <Tooltip>
      <TooltipTrigger asChild className="md:block">
        {children}
      </TooltipTrigger>
      <TooltipContent side="right" className="font-medium">
        {label}
      </TooltipContent>
    </Tooltip>
  )
}
